/**

Copyright 2019 Forestry.io Inc

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.

*/

import { FormOptions, Form, Field } from '@tinacms/forms'
import * as React from 'react'
import { usePlugins } from './use-plugin'

export interface WatchableFormValue {
  values: any
  label: FormOptions<any>['label']
  fields: FormOptions<any>['fields']
}

export function useLocalForm<FormShape = any>(
  options: FormOptions<any>,
  watch: Partial<WatchableFormValue> = {}
): [FormShape, Form] {
  const [values, form] = useForm<FormShape>(options, watch)

  usePlugins(form)

  return [values, form]
}

/**
 * @alias useLocalForm
 */
export const useCMSForm = useLocalForm

/**
 * A hook that creates a form and updates it's watched properties.
 */
export function useForm<FormShape = any>(
  { loadInitialValues, ...options }: FormOptions<any>,
  watch: Partial<WatchableFormValue> = {}
): [FormShape, Form] {
  /**
   * `initialValues` will be usually be undefined if `loadInitialValues` is used.
   *
   * If the form helper is using `watch.values`, which would contain
   * the current state of the form, then we set that to the `initialValues`
   * so the form is initialized with some state.
   *
   * This is beneficial for SSR and will hopefully not be noticeable
   * when editing the site as the actual `initialValues` will be set
   * behind the scenes.
   */
  options.initialValues = options.initialValues || watch.values

  const [, setValues] = React.useState(options.initialValues)
  const [form, setForm] = React.useState<Form>(() => {
    return createForm(options, (form: any) => {
      setValues(form.values)
    })
  })

  React.useEffect(
    function() {
      if (form.id === options.id) return
      setForm(
        createForm(options, (form: any) => {
          setValues(form.values)
        })
      )
    },
    [options.id]
  )

  React.useEffect(() => {
    if (loadInitialValues) {
      loadInitialValues().then((values: any) => {
        form.updateInitialValues(values)
      })
    }
  }, [form])

  useUpdateFormFields(form, watch.fields)
  useUpdateFormLabel(form, watch.label)
  useUpdateFormValues(form, watch.values)

  return [form ? form.values : options.initialValues, form]
}

function createForm(options: FormOptions<any>, handleChange: any): Form {
  const form = new Form(options)
  form.subscribe(handleChange, { values: true })
  return form
}

/**
 * A React Hook that update's the `Form` if `fields` are changed.
 *
 * This hook is useful when dynamically creating fields, or updating
 * them via hot module replacement.
 */
function useUpdateFormFields(form: Form, fields?: Field[]) {
  React.useEffect(() => {
    if (typeof fields === 'undefined') return
    form.updateFields(fields)
  }, [form, fields])
}

/**
 * A React Hook that update's the `Form` if the `label` is changed.
 *
 * This hook is useful when dynamically creating creating the label,
 * or updating it via hot module replacement.
 */
function useUpdateFormLabel(form: Form, label?: string) {
  React.useEffect(() => {
    if (typeof label === 'undefined') return
    form.label = label
  }, [form, label])
}

/**
 * Updates the Form with new values.
 *
 * Only updates fields that are:
 *
 * 1. registered with the form
 * 2. not currently [active](https://final-form.org/docs/final-form/types/FieldState#active)
 *
 * This hook is useful when the form must be kept in sync with the data source.
 */
function useUpdateFormValues(form: Form, values?: any) {
  React.useEffect(() => {
    if (typeof values === 'undefined') return
    form.updateValues(values)
  }, [form, values])
}
